// app/[locale]/page.tsx
"use client"

import { Button } from "@/components/ui/button"
import { AuthModal } from "@/components/auth/auth-modal"
import { useAuth } from "@/components/auth/auth-context"
import { useState, useEffect, useRef } from "react"
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import HeroSection from "@/components/HeroSection";
import FeaturesSection from "@/components/FeaturesSection";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BentoSection from "@/components/BentoSection"
import { ChatDemoShell } from "@/components/demo/ChatDemoShell"
import { TeamsCatalog } from "@/components/demo/TeamsCatalog"
import PrinciplesSection from "@/components/PrinciplesSection"
import { env } from "@/lib/env";



function HexDecor({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 115" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <polygon
        points="50,5 95,27.5 95,87.5 50,110 5,87.5 5,27.5"
        stroke="rgb(var(--accent))"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  );
}

export default function HomeClient(){
  const { isAuthenticated, isLoading } = useAuth()
  const [showAuth, setShowAuth] = useState(false)
  const router = useRouter()
  const lang = useLocale()
  const tCat = useTranslations('catalog')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="size-8 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
      </div>
    )
  }

  return (
    <>
      <Navbar />
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      <HeroSection
        onLogin={() => setShowAuth(true)}
        isAuthenticated={isAuthenticated}
        onGoToApp={() => router.push('/ai')}
      />

        {/* Three Operating Principles — closed-graph trust block */}
        <PrinciplesSection />

       <section id="teams-catalog" className="relative flex justify-center px-4 py-16 bg-base overflow-hidden">
          {/* Grid background */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(rgb(var(--accent) / 0.12) 1px, transparent 1px),
                linear-gradient(90deg, rgb(var(--accent) / 0.12) 1px, transparent 1px)
              `,
              backgroundSize: "60px 60px",
              maskImage: "radial-gradient(ellipse 60% 80% at 50% 50%, black, transparent 90%)",
              WebkitMaskImage: "radial-gradient(ellipse 60% 80% at 50% 50%, black, transparent 90%)",
            }}
          />

          {/* Radial glow */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 50% 60% at 50% 50%, rgb(var(--accent) / 0.08) 0%, transparent 70%)",
            }}
          />

          {/* Decorative hexagons */}
          <HexDecor className="absolute left-[5%] top-12 w-16 h-16 opacity-20" />
          <HexDecor className="absolute right-[7%] bottom-16 w-24 h-24 opacity-15" />
          <HexDecor className="absolute left-[12%] bottom-24 w-12 h-12 opacity-25" />
          <HexDecor className="absolute right-[15%] top-20 w-20 h-20 opacity-10" />

          {/* Chat demo */}
          <div className="relative z-10 w-full max-w-7xl px-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-fore mb-10 leading-tight text-left">
             {tCat('heading')}
           </h2>
           <p className="text-base md:text-lg text-mute max-w-3xl mb-10 leading-relaxed">
             {tCat('description')}
           </p>
           <TeamsCatalog />
          </div>
        </section>

        

      {/*<FeaturesSection />*/}
      <BentoSection />
      <Footer />
    </>
  )
}

// Мини-чат для анонимов
function AnonChatPreview() {
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState<{ role: string; content: string }[]>([])
  const [isStreaming, setIsStreaming] = useState(false)

  async function handleSend() {
    if (!input.trim() || isStreaming) return
    const msg = input.trim()
    setInput("")
    setIsStreaming(true)
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }])

    try {
      //const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"}/v1/chat/completions`, {
      const res = await fetch(`${env.apiUrl}/v1/chat/completions`, {
       method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "router-model",
          messages: [{ role: "user", content: msg }],
          userId: "anonymous",
        }),
      })

      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      if (!reader) return

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const lines = decoder.decode(value).split("\n")
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue
          const data = line.slice(6).trim()
          if (data === "[DONE]") break
          try {
            const content = JSON.parse(data)?.choices?.[0]?.delta?.content
            if (content) {
              setMessages((prev) => {
                const updated = [...prev]
                updated[updated.length - 1].content += content
                return [...updated]
              })
            }
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const updated = [...prev]
        updated[updated.length - 1].content = "Ошибка соединения"
        return [...updated]
      })
    } finally {
      setIsStreaming(false)
    }
  }

  return (
    <div className="flex flex-col gap-3 max-h-60 overflow-y-auto">
      {messages.map((m, i) => (
        <div key={i} className={`text-sm px-3 py-2 rounded-xl ${m.role === "user" ? "bg-blue-50 text-slate-800 self-end" : "bg-slate-100 text-slate-700 self-start"}`}>
          {m.content}
        </div>
      ))}
      <div className="flex gap-2 mt-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          placeholder="Спроси что-нибудь..."
          className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-blue-400"
          disabled={isStreaming}
        />
        <Button size="sm" onClick={handleSend} disabled={!input.trim() || isStreaming}>
          →
        </Button>
      </div>
    </div>
  )
}